import React, { useState } from "react";

import { AccountCircle, People } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Input,
  InputAdornment,
  Skeleton,
  Stack,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { Spacer } from "@/components/Spacer/Spacer";
import { LargeTitle, Title3Bold } from "@/components/Typography";
import { ClientFragment, OrderByDirection } from "@/gql/graphql";
import useDebounce from "@/hooks/useDebounce";

import { ClientItem } from "./ClientItem";
import { ClientStatusHeader } from "./ClientStatusHeader";

const clientStatusCheckboxes = [
  {
    value: "active",
    label: "Active",
  },
  {
    value: "struggling",
    label: "Struggling",
  },
  {
    value: "inactive",
    label: "Inactive",
  },
];

const useStyles = makeStyles()({
  iconSize: {
    height: "80px",
    width: "80px",
  },
});

interface ClientListProps {
  isLoading: boolean;
  clients: ClientFragment[];
  tab: "foodJournaling" | "weightLogging" | "modules";
  onSort: (sortBy: OrderByDirection) => void;
  onSearch: (searchKey: string) => void;
  onFilterStatus: (statuses: string[]) => void;
}

export const ClientList: React.FC<ClientListProps> = ({
  isLoading,
  clients,
  tab,
  onSort,
  onSearch,
  onFilterStatus,
}) => {
  const { classes } = useStyles();
  const [selectedClientStatusCheckboxes, setSelectedClientStatusCheckboxes] =
    useState<string[]>(["active", "struggling", "inactive"]);
  const [sortDirection, setSortDirection] = useState<OrderByDirection>(
    OrderByDirection.Asc
  );
  const [searchKey, setSearchKey] = useState("");
  const setSearchKeyDebounce = useDebounce(setSearchKey, 500);

  const handleSearch = (value: string) => {
    setSearchKeyDebounce(value);
    onSearch(value);
  };

  const handleSortDirection = () => {
    const sortDirectionValue =
      sortDirection === OrderByDirection.Asc
        ? OrderByDirection.Desc
        : OrderByDirection.Asc;
    setSortDirection(sortDirectionValue);
    onSort(sortDirectionValue);
  };

  const handleCheckboxChange = (checked: boolean, value: string) => {
    setSelectedClientStatusCheckboxes((prevSelected) => {
      if (checked) {
        onFilterStatus([...prevSelected, value]);
        return [...prevSelected, value];
      } else {
        onFilterStatus(prevSelected.filter((item) => item !== value));
        return prevSelected.filter((item) => item !== value);
      }
    });
  };

  const status = (client: ClientFragment) => {
    switch (tab) {
      case "foodJournaling":
      default:
        return client.mealActivitiesStatus;
      case "weightLogging":
        return client.weightActivitiesStatus;
      case "modules":
        return client.learningModuleActivitiesStatus;
    }
  };

  const renderTableState = () => {
    if (isLoading || !clients) {
      return (
        <Stack spacing={2}>
          {Array.from({ length: 10 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" width="100%" height={70} />
          ))}
        </Stack>
      );
    }
    if (clients.length === 0) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center">
          <Spacer h={250} />
          <Stack alignItems="center">
            <People className={classes.iconSize} color="secondary" />
            <Spacer h={24} />
            <LargeTitle text="No Clients" />
          </Stack>
        </Box>
      );
    }
    return (
      <Stack spacing={2}>
        {clients.map((client: ClientFragment, index) => (
          <ClientItem key={index} client={client} status={status(client)} />
        ))}
      </Stack>
    );
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Title3Bold
            text={`All Clients ${isLoading ? "" : `(${clients.length})`}`}
          />
        </Box>
        <Box display="flex">
          <FormGroup row>
            {clientStatusCheckboxes.map((clientStatus) => (
              <FormControlLabel
                key={clientStatus.value}
                control={<Checkbox />}
                checked={selectedClientStatusCheckboxes.includes(
                  clientStatus.value
                )}
                value={clientStatus.value}
                label={clientStatus.label}
                onChange={(_, checked) =>
                  handleCheckboxChange(checked, clientStatus.value)
                }
              />
            ))}
          </FormGroup>
          <Box width="300px">
            <Input
              placeholder="Search Client"
              startAdornment={
                <InputAdornment position="start">
                  <AccountCircle />
                </InputAdornment>
              }
              value={searchKey}
              onChange={(evt) => {
                handleSearch(evt.target.value);
              }}
            />
          </Box>
        </Box>
      </Box>
      <Spacer h={12} />
      <Box>
        <ClientStatusHeader
          sortDirection={sortDirection}
          onSortButtonClick={handleSortDirection}
        />
        <Spacer h={12} />
      </Box>
      {renderTableState()}
    </>
  );
};
